<template>
    <PageNavigation v-bind="navigation" :section-titles="sectionsRef" />
    <section
        v-for="section in sections"
        :key="section.id"
        class="blog-section col-span-full -mt-px rounded-none bg-white px-4 py-4 first-of-type:pt-0 lg:col-span-8 lg:col-start-5 lg:px-12">
        <hr v-show="section.hasLine" class="border-brand-black/10 order-3 -mt-4 mb-4" />
        <h2
            :id="section.id"
            ref="sections"
            data-section-title
            class="mb-2 scroll-m-30 font-semibold lg:scroll-m-6">
            {{ section.title }}
        </h2>
        <div v-if="'body' in section" class="rte grid gap-4" v-html="section.body" />

        <template v-if="'blocks' in section">
            <div v-for="(block, index) in section.blocks" :key="`${block.alias}-${index}`">
                <div
                    v-if="block.alias === 'richTextBlock' && 'body' in block"
                    class="rte grid gap-4"
                    v-html="block.body" />
                <div
                    v-if="block.alias === 'imagesBlock' && 'images' in block"
                    class="mt-4 grid gap-4">
                    <img
                        v-for="image in block.images"
                        :key="image.url ?? index"
                        :src="image.url"
                        :alt="image.alt ?? ''"
                        class="max-h-62.5 w-full rounded-lg object-cover lg:w-[calc(50%-8px)]" />
                </div>
            </div>
        </template>

        <div v-if="'button' in section && section.button">
            <NuxtLink class="btn-secondary-a mt-4" :to="localeRoute(section.button?.url ?? '/')">
                {{ section.button?.title }}
            </NuxtLink>
        </div>
        <ul v-if="'items' in section" class="rte">
            <li v-for="(item, index) in section.items" :key="`${item?.title}-${index}`">
                <a class="btn-tertiary-a" :href="item?.link?.url">{{ item?.title }}</a>
            </li>
        </ul>
    </section>
</template>

<script setup lang="ts">
import type { BlogSectionsComponentModel } from '@su4r-frontend/api';
import PageNavigation from '../navigation/PageNavigation.vue';

defineProps<BlogSectionsComponentModel>();

const sectionsRef = useTemplateRef<HTMLHeadingElement[]>('sections');
const route = useRoute();
const localeRoute = useLocaleRoute();

onMounted(() => {
    if (!route.hash) return;
    const section = document.querySelector(route.hash);

    if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
    }
});
</script>

<style lang="css">
@reference "~/assets/css/main.css";

.blog-section:last-child {
    @apply rounded-b-lg pb-12;
}

.rte {
    h2 {
        @apply text-3xl font-semibold;
    }

    h3 {
        @apply text-xl font-semibold;
    }

    h4 {
        @apply text-lg font-semibold;
    }

    a {
        @apply btn-tertiary-a mr-1 -mb-4 px-0 text-left;

        &::after {
            width: 100%;
            left: 0;
        }
    }

    ul {
        @apply list-disc pl-6;

        a {
            @apply mb-0 h-auto pb-3.25;

            &::after {
                bottom: unset;
                top: calc(100% - 9px);
                transition: top 0.2s ease;
            }

            &:hover::after {
                top: calc(100% - 13px);
            }
        }
    }
}
</style>
